/**
 * TSV Discovery — collection page handler
 *
 * Lives in assets/tsv-discovery.js, loaded by sections/tsv-discovery-bottles.liquid.
 *
 * Responsibilities:
 *   1. Qty stepper (− / +) on each product row
 *   2. AJAX Add to Bag — POST /cart/add.js, then trigger Dawn's cart drawer/
 *      notification via the standard 'cart:refresh' + cart-notification events
 *   3. Reward banner — reads localStorage `tsv_reward_code` (set by the
 *      redeem page) and shows the customer's code with a copy button
 *   4. Title splitter — moves "EDP 125ml" into a mono subtitle line,
 *      matching the PDP behaviour
 */
(function () {
  // Locale lookup. Strings come from locales/*.json via snippets/tsv-i18n-js.liquid.
  function T(key, fallback) { return (window.TSV_T && window.TSV_T[key]) || fallback; }

  'use strict';

  var LOCAL_KEY = 'tsv_reward_code';
  var LOCAL_META_KEY = 'tsv_reward_code_meta';

  function init(root) {
    if (!root || root.__tsvBottlesInitDone) return;
    root.__tsvBottlesInitDone = true;

    initBanner(root);
    initRowReveal(root);

    root.querySelectorAll('[data-tsv-product]').forEach(function (row) {
      initRow(row);
      splitTitle(row);
    });
  }

  /* ─── Scroll reveal ───
     Each product row fades up as it enters the viewport. Bails out cleanly
     on reduced-motion preference or if IntersectionObserver isn't supported. */
  function initRowReveal(root) {
    var rows = root.querySelectorAll('[data-tsv-product]');
    var reduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      rows.forEach(function (r) { r.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -60px 0px' });
    rows.forEach(function (r) { observer.observe(r); });
  }

  /* ─── Title splitter ───
     Mirrors the PDP behaviour. Takes "Le Male le Parfum EDP 125ml" and
     turns it into name "Le Male le Parfum" + subtitle "Eau de Parfum · 125ml".
     Keeps the bold product name clean and pushes the concentration/size
     into a small mono subtitle line underneath. */
  var TSV_CONC_MAP = {
    'edp': 'Eau de Parfum',
    'edt': 'Eau de Toilette',
    'edc': 'Eau de Cologne',
    'eds': 'Eau de Senteur',
    'parfum': 'Parfum',
    'elixir': 'Elixir',
    'extrait': 'Extrait',
    'cologne': 'Cologne'
  };
  /* Two regexes so abbreviations (EDP, EDT, EDC, EDS) always win over the
     longer concentration words. Without this, a title like
     "Le Male le Parfum EDP 125ml" matches "Parfum" first and splits the
     name in the wrong place. */
  var TSV_CONC_ABBR_RE = /\b(EDP|EDT|EDC|EDS)\b(?=(?:\s*(?:limited\s+edition|\(?\s*\d+(?:\.\d+)?\s*(?:[x×]\s*\d+(?:\.\d+)?\s*)?ml\s*\)?|\+\s*\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?\s*ml|(?:gift|discovery|travel)\s+set|duo|set))*\s*$)/i;
  var TSV_CONC_FULL_RE = /\b(Eau\s+de\s+Parfum(?:\s+Intense|\s+Fra[iî]che)?|Eau\s+de\s+Toilette(?:\s+Intense)?|Eau\s+de\s+Cologne|Eau\s+de\s+Senteur|Eau\s+Fra[iî]che|Extrait\s+de\s+Parfum|Parfum|Elixir|Extrait|Cologne)\b(?=(?:\s*(?:limited\s+edition|\(?\s*\d+(?:\.\d+)?\s*(?:[x×]\s*\d+(?:\.\d+)?\s*)?ml\s*\)?|\+\s*\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?\s*ml|(?:gift|discovery|travel)\s+set|duo|set))*\s*$)/i;
  var TSV_SIZE_RE = /\(?\s*(\d+(?:\.\d+)?(?:\s*[x×]\s*\d+(?:\.\d+)?)?)\s*ml\b\s*\)?/i;
  /* Product-type suffix — identical list to tsv-search.js so every surface splits alike. */
  var TSV_TYPE_RE = /\b(Deodorant\s+Stick|Deodorant\s+Spray|Deodorant|Roll[- ]?On|Bath\s*&\s*Shower\s+Gel|Shower\s+Gel|Shower\s+Cream|Bath\s+Gel|Body\s+Lotion|Body\s+Cream|Body\s+Butter|Body\s+Spray|Body\s+Wash|Body\s+Oil|Hair\s*&\s*Body\s+Mist|Body\s+Mist|Hair\s+Mist|Hand\s+Cream|After\s*shave\s+Balm|After\s*shave\s+Lotion|After\s*shave|Gift\s+Set|Discovery\s+Set|Travel\s+Set|Duo|Beard\s+Oil|Lotion|Soap|Candle)\b/i;

  function splitTitle(row) {
    var titleEl = row.querySelector('[data-tsv-title]');
    var subEl = row.querySelector('[data-tsv-subtitle]');
    if (!titleEl || !subEl) return;

    var titleText = (titleEl.textContent || '').replace(/\s+/g, ' ').trim();
    if (!titleText) return;

    var name = titleText;
    var parts = [];
    // Prefer the abbreviation (EDP/EDT) over the longer word so product
    // names containing "Parfum" or "Cologne" don't get mis-split.
    var concMatch = titleText.match(TSV_CONC_ABBR_RE) || titleText.match(TSV_CONC_FULL_RE);

    if (concMatch) {
      var idx = concMatch.index;
      name = titleText.slice(0, idx).replace(/[\s,·\-–]+$/, '').trim();
      var rest = titleText.slice(idx + concMatch[0].length).trim();
      parts.push(TSV_CONC_MAP[concMatch[1].toLowerCase()] || concMatch[1]);

      var sizeMatch = rest.match(TSV_SIZE_RE) || titleText.match(TSV_SIZE_RE);
      if (sizeMatch) {
        parts.push(sizeMatch[1] + 'ml');
        rest = rest.replace(TSV_SIZE_RE, '').trim();
      }
      rest = rest.replace(/\b(EDP|EDT|EDC|EDS|Parfum|Elixir|Extrait|Cologne|Eau\s+de\s+\w+)\b/ig, '');
      rest = rest.replace(/[\s,·\-–]+/g, ' ').trim();
      if (rest) parts.push(rest);

      if (!name) { name = titleText; parts = []; }
    } else if (titleText.match(TSV_TYPE_RE)) {
      var tmatch = titleText.match(TSV_TYPE_RE);
      name = titleText.slice(0, tmatch.index).replace(/[\s,·\-–]+$/, '').trim();
      parts.push(tmatch[1]);
      var tsm = titleText.match(TSV_SIZE_RE);
      if (tsm) parts.push(tsm[1] + 'ml');
      if (!name) { name = titleText; parts = []; }
    } else {
      var sm = titleText.match(TSV_SIZE_RE);
      if (sm) {
        name = titleText.replace(TSV_SIZE_RE, '').replace(/[\s,·\-–]+$/, '').trim();
        parts.push(sm[1] + 'ml');
      }
    }

    titleEl.textContent = name || titleText;
    var subtitle = parts.join(' · ');
    if (subtitle) {
      subEl.textContent = subtitle;
      subEl.hidden = false;
      row.classList.add('is-titled');
    }
  }

  /* ─── Reward banner ─── */
  function initBanner(root) {
    var banner = root.querySelector('[data-tsv-banner]');
    var textEl = root.querySelector('[data-tsv-banner-text]');
    var copyBtn = root.querySelector('[data-tsv-banner-copy]');
    if (!banner || !textEl) return;

    var code = '';
    var meta = null;
    try { code = localStorage.getItem(LOCAL_KEY) || ''; } catch (_) {}
    try { meta = JSON.parse(localStorage.getItem(LOCAL_META_KEY) || 'null'); } catch (_) {}

    if (code) {
      // Has a code. Show banner with the code + Copy button, flip the
      // section into "is-rewarded" so each row's reward pill renders.
      banner.classList.add('is-claimed');
      root.classList.add('is-rewarded');
      var amount = (meta && meta.amount) ? meta.amount : 10;
      textEl.innerHTML = '<strong>Your €' + escapeHtml(amount) +
        ' reward is ready</strong> · ' +
        '<span class="tsv-bottles__banner-code">' + escapeHtml(code) + '</span>' +
        T('dcj_applied_at_checkout',' · applied at checkout');
      if (copyBtn) {
        copyBtn.hidden = false;
        copyBtn.addEventListener('click', function () { copyToClipboard(code, copyBtn); });
      }
    } else {
      // No code. Offer the redeem link.
      var redeemUrl = root.getAttribute('data-redeem-url') || '/pages/redeem';
      textEl.innerHTML = 'Have a Discovery Collection Reward? <a href="' +
        escapeHtml(redeemUrl) + '">Redeem your code →</a>';
    }
  }

  /* ─── Per-product row ─── */
  function initRow(row) {
    var form = row.querySelector('[data-tsv-form]');
    if (!form) return;
    var qtyInput = row.querySelector('[data-tsv-qty]');
    var minusBtn = row.querySelector('[data-tsv-qty-minus]');
    var plusBtn = row.querySelector('[data-tsv-qty-plus]');
    var atcBtn = row.querySelector('[data-tsv-atc]');
    var errEl = row.querySelector('[data-tsv-err]');

    if (minusBtn) minusBtn.addEventListener('click', function () { stepQty(qtyInput, -1); });
    if (plusBtn) plusBtn.addEventListener('click', function () { stepQty(qtyInput, 1); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitForm(form, atcBtn, errEl);
    });
  }

  function stepQty(input, delta) {
    if (!input) return;
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) v = 1;
    v = Math.max(1, v + delta);
    input.value = v;
  }

  function submitForm(form, atcBtn, errEl) {
    hideError(errEl);
    if (atcBtn) {
      atcBtn.classList.add('is-loading');
      atcBtn.disabled = true;
    }

    var origLabel = atcBtn ? atcBtn.textContent : '';

    // Build /cart/add.js body. Include the standard `sections` param so
    // Dawn's cart drawer / notification can refresh its DOM in one call.
    var sectionsList = getDawnCartSections();
    var formData = new FormData(form);
    if (sectionsList) formData.append('sections', sectionsList);
    formData.append('sections_url', window.location.pathname);

    fetch('/cart/add.js', { method: 'POST', body: formData, credentials: 'same-origin' })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      })
      .then(function (data) {
        // Refresh Dawn cart UI
        notifyDawnCart(data);

        // Visual confirmation on the button
        if (atcBtn) {
          atcBtn.classList.remove('is-loading');
          atcBtn.classList.add('is-added');
          atcBtn.textContent = 'Added ✓';
          setTimeout(function () {
            atcBtn.classList.remove('is-added');
            atcBtn.textContent = origLabel;
            atcBtn.disabled = false;
          }, 1800);
        }
      })
      .catch(function (err) {
        if (atcBtn) {
          atcBtn.classList.remove('is-loading');
          atcBtn.disabled = false;
        }
        var msg = (err && err.description) || (err && err.message) ||
                  T('pdj_add_to_bag_failed', 'Couldn\'t add to bag. Please try again.');
        showError(errEl, msg);
      });
  }

  /* ─── Dawn integration ─── */
  function getDawnCartSections() {
    // Dawn's cart-notification + cart-drawer expose a getSectionsToRender()
    // method. Try both. Fall back to common section names.
    var n = document.querySelector('cart-notification, cart-drawer');
    if (n && typeof n.getSectionsToRender === 'function') {
      try {
        return n.getSectionsToRender().map(function (s) { return s.section; }).join(',');
      } catch (_) {}
    }
    return 'cart-notification,cart-drawer,cart-icon-bubble,cart-live-region-text,main-cart-items,main-cart-footer';
  }

  function notifyDawnCart(addResponse) {
    // Update Dawn cart icon + drawer/notification by passing the new sections
    var n = document.querySelector('cart-notification, cart-drawer');
    if (n && typeof n.renderContents === 'function') {
      try { n.renderContents(addResponse); return; } catch (_) {}
    }
    // Fallback: just open whatever's there
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') { try { drawer.open(); } catch (_) {} }
    // And let other listeners (theme custom JS) refresh themselves
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /* ─── helpers ─── */
  function showError(el, msg) { if (!el) return; el.textContent = msg; el.hidden = false; }
  function hideError(el) { if (!el) return; el.textContent = ''; el.hidden = true; }

  function copyToClipboard(text, btn) {
    var done = function () {
      if (!btn) return;
      var orig = btn.textContent;
      btn.classList.add('is-copied');
      btn.textContent = 'Copied';
      setTimeout(function () {
        btn.classList.remove('is-copied');
        btn.textContent = orig;
      }, 1800);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(fallback);
    } else { fallback(); }
    function fallback() {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select(); document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch (_) {}
    }
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function boot() { document.querySelectorAll('[data-tsv-bottles]').forEach(init); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
  document.addEventListener('shopify:section:load', boot);
})();